/**
 * Utilitários para cálculos de diferenças do caixa
 * Calcula dinamicamente as diferenças entre valores esperados e informados
 */
import { formatPriceWithSymbol } from './currency';
import i18n from '../i18n';

/**
 * Calcula a diferença entre valor informado e esperado
 * @param {number} valorInformado - Valor que foi informado no fechamento
 * @param {number} valorEsperado - Valor que o sistema esperava
 * @returns {number} Diferença (positivo = sobrou, negativo = faltou)
 */
export const calcularDiferenca = (valorInformado, valorEsperado) => {
  return (valorInformado || 0) - (valorEsperado || 0);
};

/**
 * Calcula todas as diferenças de um caixa
 * @param {object} caixa - Objeto do caixa com valores esperados e informados
 * @returns {object} Objeto com todas as diferenças calculadas
 */
export const calcularDiferencasCaixa = (caixa) => {
  // Para DINHEIRO: comparar valor informado com saldo físico esperado
  // Saldo Físico = Saldo Inicial + Vendas + Suprimentos - Sangrias
  let saldo_fisico_esperado_dinheiro = caixa.saldo_inicial || 0;
  
  // Calcular movimentações físicas de dinheiro através dos lançamentos
  if (caixa.lancamentos_caixa && Array.isArray(caixa.lancamentos_caixa)) {
    caixa.lancamentos_caixa.forEach(lancamento => {
      const tipoNormalizado = normalizePaymentType(lancamento.tipo_lancamento);
      // Incluir todas movimentações de dinheiro EXCETO o saldo inicial
      if (tipoNormalizado === "Dinheiro" && lancamento.descricao !== "Saldo Inicial") {
        saldo_fisico_esperado_dinheiro += lancamento.valor || 0;
      }
    });
  }

  const diferenca_dinheiro = calcularDiferenca(
    caixa.valor_informado_dinheiro,
    saldo_fisico_esperado_dinheiro // Usar saldo físico ao invés de valor esperado
  );
  
  // Para PIX e Cartão: usar valores esperados normalmente (apenas vendas)
  const diferenca_cartao = calcularDiferenca(
    caixa.valor_informado_cartao,
    caixa.valor_esperado_cartao
  );
  
  const diferenca_pix = calcularDiferenca(
    caixa.valor_informado_pix,
    caixa.valor_esperado_pix
  );
  
  const diferenca_total = diferenca_dinheiro + diferenca_cartao + diferenca_pix;
  
  return {
    diferenca_dinheiro,
    diferenca_cartao,
    diferenca_pix,
    diferenca_total,
    saldo_fisico_esperado_dinheiro // Para debug
  };
};

/**
 * Normaliza tipo de pagamento (igual ao backend)
 * @param {string} type - Tipo de pagamento
 * @returns {string} Tipo normalizado
 */
const normalizePaymentType = (type) => {
  const t = (type || "").toString().trim().toLowerCase();
  if (t.includes("pix")) return "Pix";
  if (t.includes("cart")) return "Cartão";
  if (t.includes("dinheiro") || t.includes("cash")) return "Dinheiro";
  return "Dinheiro";
};

/**
 * Calcula os totais esperados e informados
 * @param {object} caixa - Objeto do caixa
 * @returns {object} Totais calculados
 */
export const calcularTotaisCaixa = (caixa) => {
  const total_esperado = (caixa.valor_esperado_dinheiro || 0) + 
                        (caixa.valor_esperado_cartao || 0) + 
                        (caixa.valor_esperado_pix || 0);
  
  const total_informado = (caixa.valor_informado_dinheiro || 0) + 
                         (caixa.valor_informado_cartao || 0) + 
                         (caixa.valor_informado_pix || 0);
  
  return {
    total_esperado,
    total_informado
  };
};

/**
 * Determina a classe CSS para uma diferença
 * @param {number} diferenca - Valor da diferença
 * @returns {string} Classe CSS apropriada
 */
export const getClasseDiferenca = (diferenca) => {
  if (diferenca === 0) return '';
  return diferenca > 0 ? 'positive' : 'negative';
};

/**
 * Formata uma diferença para exibição
 * @param {number} diferenca - Valor da diferença
 * @param {function} formatPrice - Função de formatação de preço
 * @returns {string} Diferença formatada
 */
export const formatarDiferenca = (diferenca, formatPrice) => {
  if (diferenca === 0) return i18n.t('cashRegister:differences.matches');
  const sinal = diferenca > 0 ? '+' : '';
  return sinal + formatPrice(diferenca);
};

/**
 * Gera mensagem explicativa para a diferença total
 * @param {number} diferencaTotal - Valor da diferença total
 * @param {function} formatPrice - Função de formatação de preço
 * @returns {string} Mensagem explicativa
 */
// O segundo parametro existe so por compatibilidade com os chamadores antigos:
// a formatacao agora sai do currency.js, que conhece a moeda vigente. O texto
// estava cravado em portugues e o simbolo era concatenado a mao.
export const getMensagemDiferenca = (diferencaTotal, _formatPriceLegado) => {
  if (diferencaTotal === 0) return '';

  const valor = formatPriceWithSymbol(Math.abs(diferencaTotal));
  return diferencaTotal > 0
    ? i18n.t('cashRegister:differences.surplus', { amount: valor })
    : i18n.t('cashRegister:differences.shortage', { amount: valor });
};
